import { useQuery } from 'react-query';
import { authFetch } from '../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../CommonUtils/urlWithParams';
import { queryKeys } from './queryKeys';

export enum IncomeType {
  Salary,
  Bonus,
  E401KContribution,
  Pension,
  Business,
  RealEstate,
  SocialSecurity,
  Other,
}

export enum IncomeFrequency {
  Yearly,
  Monthly,
}

export interface IncomeModel {
  Id: string;
  Name: string;
  IncomeType: IncomeType;
  Earner: string;
  Amount: number | null;
  IncomeFrequency: IncomeFrequency | null;
  IsSpouse: boolean;
  BonusCommissions: number | null;
  AssociatedAssetId: string;
  ContinuesPostRetirement: boolean | null;
  StartAge: number | null;
  EndAge: number | null;
  Inflation: number | null;
  IsTaxFree: boolean | null;
  IncomeGrowth: number | null;
  SelfEmployed: boolean | null;
  AccountEarmark: { Type: string; Tooltip: string }[];
  YearlyTaxableAmount: number | null;
  EndAtRetirement: boolean | null;
  EndAtEndOfPlan: boolean | null;
  CurrentlyReceivingBenefits: boolean;
  UseFra: boolean | null;
  OrionManagementStyleId: number | null;
  CustomBucketIndex: number | null;
}

export interface ManagementStyleDTO {
  id: number;
  name: string;
  defaultBucket: number | null;
}

interface AvailableAssetBucketsDTO {
  AssetTypes: number[];
  Description: string;
  ManagementStyles: number[];
  Title: string;
}

export interface IncomeOptionsDTO {
  AgesOptions: number[];
  BusinessAssetOption: { label: string; value: string }[];
  EarmarkOptions: { label: string; value: string }[];
  EndYearOptions: number[];
  IncomeFrequencyOptions: { label: string; value: IncomeFrequency }[];
  IncomeTypeOptions: { label: string; value: IncomeType }[];
  OtherIncomeEndYearOptions: number[];
  OwnerOptions: string[];
  OwnerOptionsSocSec: string[];
  RealEstateAssetOptions: { label: string; value: string }[];
  SocialSecurityAgeOptions: number[];
  StartYearOptions: number[];
  PrincipalSocialSecurityAgeOptions: number[];
  SpouseSocialSecurityAgeOptions: number[];
  PrincipalFra: { Year: number; Month: number };
  SpouseFra: { Year: number; Month: number };

  AssetBucketingByManagementStyle: boolean;
  ManagementStyles: ManagementStyleDTO[];
  AllowAssetBucketingCustomization: boolean;
  AvailableAssetBuckets: AvailableAssetBucketsDTO[];
  DisplayAssetBucketing: boolean;
}

export interface IncomeDTO {
  Incomes: IncomeModel[];
  Options: IncomeOptionsDTO;
  SocialSecurityUseSpousalBenefit: boolean;
}

interface FetchParams {
  clientId: string;
}

export const fetchIncome = async ({ clientId }: FetchParams): Promise<IncomeDTO> => {
  return authFetch(urlWithParams('/Consumer/IncomeStep', { clientId }));
};

interface QueryParams {
  clientId: string;
}

export const useIncomeQuery = ({ clientId }: QueryParams) => {
  return useQuery(
    queryKeys.income(clientId),

    () => fetchIncome({ clientId }),

    {
      cacheTime: 0,
    },
  );
};
